import { searchApiClient } from "./config";
import {
  MetadataResponse,
  AssetData,
  ParsedMetadata,
  SearchResult,
} from "./types";

const normalizeId = (id: number | { low: number; high: number }): number => {
  if (typeof id === "number") return id;
  return id.low;
};

const parseMetadata = (metadataRaw: string): ParsedMetadata => {
  try {
    return JSON.parse(metadataRaw);
  } catch (error) {
    console.error("Error parsing metadata:", error);
    return { title: "Unknown Asset", description: "Metadata parsing error" };
  }
};

const determineStatus = (metadata: ParsedMetadata): string => {
  if (metadata.status) return metadata.status;
  if (metadata.lifecycle_stage) return metadata.lifecycle_stage;
  if (metadata.decommissioned) return "decommissioned";
  if (metadata.maintenance) return "maintenance";
  if (metadata.deployed) return "deployed";
  if (metadata.verified) return "verified";
  if (metadata.active) return "active";

  return "created";
};

/**
 * Generate a title for the event based on metadata
 * @param metadata - Parsed metadata
 * @param status - Determined status
 * @returns Event title
 */
const generateEventTitle = (
  metadata: ParsedMetadata,
  status: string
): string => {
  if (metadata.event_title) return metadata.event_title;
  if (metadata.title) return metadata.title;

  // Default titles based on status
  switch (status) {
    case "created":
      return "Asset Registered";
    case "verified":
      return "Hardware Verification";
    case "deployed":
      return "Deployed to Production";
    case "active":
      return "Asset Active";
    case "maintenance":
      return "Maintenance Performed";
    case "decommissioned":
      return "Asset Decommissioned";
    default:
      return "Asset Update";
  }
};

/**
 * Generate a description for the event based on metadata
 * @param metadata - Parsed metadata
 * @param status - Determined status
 * @returns Event description
 */
const generateEventDescription = (
  metadata: ParsedMetadata,
  status: string
): string => {
  if (metadata.event_description) return metadata.event_description;
  if (metadata.description) return metadata.description;

  // Extract relevant info from metadata
  const assetType =
    metadata.type || metadata.asset_type || metadata.deviceType || "Asset";
  const serialNumber =
    metadata.serial_number || metadata.serialNumber || "Unknown";

  // Default descriptions based on status
  switch (status) {
    case "created":
      return `${assetType} with serial number ${serialNumber} was registered in the system`;
    case "verified":
      return `Hardware specifications verified and recorded`;
    case "deployed":
      return `Asset was deployed to production environment`;
    case "active":
      return `Asset is currently active and in use`;
    case "maintenance":
      return `Maintenance was performed on this asset`;
    case "decommissioned":
      return `Asset has been decommissioned and is no longer in use`;
    default:
      return `Asset information was updated`;
  }
};

/**
 * Transform API response into a structured asset data object
 * @param responses - Array of metadata responses from API
 * @returns Structured asset data
 */
const transformApiResponse = (responses: MetadataResponse[]): AssetData => {
  // Sort responses by created_at date if available
  const sortedResponses = [...responses].sort((a, b) => {
    const metadataA = parseMetadata(a.metadata_raw);
    const metadataB = parseMetadata(b.metadata_raw);

    const dateA = metadataA.created_at
      ? new Date(metadataA.created_at).getTime()
      : 0;
    const dateB = metadataB.created_at
      ? new Date(metadataB.created_at).getTime()
      : 0;

    return dateB - dateA; // Reverse chronological order
  });

  const events = sortedResponses.map((response) => {
    const metadata = parseMetadata(response.metadata_raw);
    const status = determineStatus(metadata);
    const date =
      metadata.created_at || metadata.timestamp || new Date().toISOString();
    const title = generateEventTitle(metadata, status);
    const description = generateEventDescription(metadata, status);

    return {
      id: normalizeId(response.metadata_id).toString(),
      title: title,
      date,
      description: description,
      status,
      metadata,
      hash: response.metadata_hash,
    };
  });

  const latestResponse = sortedResponses[0];
  const latestMetadata = parseMetadata(latestResponse.metadata_raw);
  const placeholderImage =
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiMzMzMiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIzMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiNmZmYiPklUIEFzc2V0PC90ZXh0Pjwvc3ZnPg==";

  return {
    serialNumber:
      latestMetadata.deviceInfo?.serialNumber ||
      latestMetadata.serial_number ||
      latestMetadata.serialNumber ||
      "Unknown",
    events,
    deviceInfo: {
      partNumber:
        latestMetadata.deviceInfo?.partNumber ||
        latestMetadata.part_number ||
        latestMetadata.partNumber,
      serialNumber:
        latestMetadata.deviceInfo?.serialNumber ||
        latestMetadata.serial_number ||
        latestMetadata.serialNumber,
      ram:
        latestMetadata.deviceInfo?.ram ||
        latestMetadata.ram ||
        latestMetadata.memory,
      type:
        latestMetadata.deviceInfo?.type ||
        latestMetadata.type ||
        latestMetadata.asset_type ||
        latestMetadata.deviceType,
      confidence:
        latestMetadata.deviceInfo?.confidence || latestMetadata.confidence,
      aiEnhanced:
        latestMetadata.deviceInfo?.aiEnhanced || !!latestMetadata.ai_enhanced,
    },
    image: latestMetadata.image || placeholderImage,
    timestamp:
      latestMetadata.created_at ||
      latestMetadata.timestamp ||
      new Date().toISOString(),
    hash: latestResponse.metadata_hash,
    hashAlg: "SHA-256",
  };
};

export const searchService = {
  search: async (query: string): Promise<SearchResult[]> => {
    try {
      const encodedQuery = encodeURIComponent(query);
      const response = await searchApiClient.get<SearchResult[]>(
        `/api/neo4j/metadata/search?query=${encodedQuery}`
      );
      console.log(`Search results:`, response.data);
      return response.data;
    } catch (error) {
      console.error("Error searching assets:", error);
      throw error;
    }
  },

  getAssetData: async (query: string): Promise<AssetData> => {
    try {
      console.log("Fetching asset data for query:", query);
      const results = await searchService.search(query);

      if (!results || !Array.isArray(results) || results.length === 0) {
        throw new Error("No asset data found for the given query");
      }

      const response = transformApiResponse(results);
      console.log("Asset data:", response);

      return response;
    } catch (error) {
      console.error("Error fetching asset data:", error);
      throw error;
    }
  },

  createSearchRecord: async (
    metadata: ParsedMetadata
  ): Promise<SearchResult> => {
    try {
      // Check fields
      if (!metadata.timestamp) {
        metadata.timestamp = new Date().toISOString();
      }

      if (!metadata.hash) {
        // FIXME: Generate a proper hash
        metadata.hash = `SHA256-${Date.now()}`;
      }

      // Ensure deviceInfo is properly structured
      if (metadata.deviceInfo) {
        const deviceInfo = metadata.deviceInfo;
        if (!deviceInfo.serialNumber && metadata.serialNumber) {
          deviceInfo.serialNumber = metadata.serialNumber;
        }
        if (!deviceInfo.type && metadata.deviceType) {
          deviceInfo.type = metadata.deviceType;
        }
        if (deviceInfo.confidence === undefined) {
          deviceInfo.confidence = 0.95;
        }
        if (deviceInfo.aiEnhanced === undefined) {
          deviceInfo.aiEnhanced = true;
        }
      } else if (metadata.serialNumber) {
        // If deviceInfo doesn't exist but we have a serialNumber, create it
        metadata.deviceInfo = {
          serialNumber: metadata.serialNumber,
          partNumber: metadata.partNumber || "",
          type: metadata.deviceType || "Unknown Device",
          ram: metadata.ram || "",
          confidence: 0.95,
          aiEnhanced: true,
        };
      }

      metadata.recordType = metadata.recordType || "device";
      metadata.source = metadata.source || "scanner";
      metadata.hashAlg = metadata.hashAlg || "SHA256";

      const metadataRecord = {
        metadata_raw: JSON.stringify(metadata),
      };

      // 1. Create the metadata record via search api
      const metadataResponse = await searchApiClient.post<SearchResult>(
        "/api/neo4j/metadata",
        metadataRecord
      );

      // 2. Create a search entry that references the metadata
      const result = { ...metadataResponse.data };

      // Only create a search entry if we have a metadata_id
      if (metadataResponse.data && metadataResponse.data.metadata_id) {
        try {
          // Prepare the search entry payload
          const searchEntry = {
            metadata_id: metadataResponse.data.metadata_id,
            dam_id: null, // We don't have a digital asset yet
            current_hash: metadata.hash, // Use the hash from our metadata
          };

          // Attempt to create the search entry
          try {
            const searchResponse = await searchApiClient.post<any>(
              "/api/neo4j/search",
              searchEntry
            );

            // If successful, update the result with search entry data
            if (searchResponse.data && searchResponse.data.search_id) {
              result.search_id = searchResponse.data.search_id;
              result.dam_id = searchResponse.data.dam_id;
              result.dam_hash = searchResponse.data.dam_hash;
              result.block_id = searchResponse.data.block_id;
              result.current_hash =
                searchResponse.data.current_hash || metadata.hash;
            }
          } catch (searchError) {
            // Log the error but continue - we'll return the metadata response
            console.error("Error creating search entry:", searchError);

            // Add placeholder values so the UI doesn't show nulls
            // Using a numeric ID to match the expected type
            result.search_id = Date.now();
            result.current_hash = metadata.hash;
          }
        } catch (error) {
          console.error("Error preparing search entry:", error);
        }
      } else {
        console.error(
          "Metadata response doesn't contain metadata_id:",
          metadataResponse.data
        );
      }

      // Return always...?
      return result;
    } catch (error) {
      console.error("Error creating search record:", error);
      throw error;
    }
  },
};

// Utils
export const searchUtils = {
  parseMetadata,
  normalizeId,
};
